import { defineComponent as _defineComponent } from 'vue'
import { isTouchScreen } from '@@/bits/browser'
import { ARMS_CROSSED_IN_X_ICON } from '@@/bits/confirmation_dialog'
import type { HTMLStyleElementWrapper } from '@@/bits/css'
import { appendCssToDocumentHead } from '@@/bits/css'
import { dir } from '@@/bits/current_dir'
import device from '@@/bits/device'
import { isActiveElementAnInputField } from '@@/bits/dom'
import window from '@@/bits/global'
import { __ } from '@@/bits/intl'
import { safeLocalStorage } from '@@/bits/safe_storage'
import { defineAsyncComponent } from '@@/bits/surface_vue'
import { getEditorObject, setEditorObject } from '@@/bits/tldraw/editor'
import type { PageMeta, TldrawDefaultColorStyleColor, TldrawGeoShapeGeoStyle } from '@@/bits/whiteboard'
import {
  ALL_TOOLS,
  ActionType,
  BackgroundType,
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  FRAME_ID_QUERY_PARAM,
  actionShortcuts,
  approveShape,
  changeAllTldrawDefaultColorStyleColor,
  geoToolColors,
  getFramePermissionOverride,
  getTldrawToolId,
  handleAction,
  isShapeApproved,
} from '@@/bits/whiteboard'
import { runScript } from '@@/bits/whiteboard_blockly_script_executor'
import { initializeLinking, listenToLinkingChanges, triggerLinking } from '@@/bits/whiteboard_linking'
import { VIEWER_TOOL_IDS, WRITER_TOOL_IDS } from '@@/bits/whiteboard_permissions'
import type { Anchor } from '@@/library/v4/components/OzBasePopover.vue'
import OzBasePopover, { OzBasePopoverColors } from '@@/library/v4/components/OzBasePopover.vue'
import OzBox, { OzBoxColors } from '@@/library/v4/components/OzBox.vue'
import OzContainedButton, {
  OzContainedButtonColorScheme,
  OzContainedButtonSizePreset,
} from '@@/library/v4/components/OzContainedButton.vue'
import OzDivider, { OzDividerColorScheme } from '@@/library/v4/components/OzDivider.vue'
import type { OzMenuRowsRow } from '@@/library/v4/components/OzMenuRows.vue'
import OzMenuRows, {
  OzMenuRowsFocusStyle,
  OzMenuRowsRowStyle,
  OzMenuRowsSizePreset,
} from '@@/library/v4/components/OzMenuRows.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from '@@/library/v4/components/OzPlainButton.vue'
import OzPopover from '@@/library/v4/components/OzPopover.vue'
import { useGlobalAlertDialogStore } from '@@/pinia/global_alert_dialog'
import {
  OzConfirmationDialogBoxButtonScheme,
  useGlobalConfirmationDialogStore,
} from '@@/pinia/global_confirmation_dialog'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { usePersonalPlansStore } from '@@/pinia/personal_plans_store'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfaceSettingsStore } from '@@/pinia/surface_settings'
import { useSurfaceSharePanelStore } from '@@/pinia/surface_share_panel'
import { useSurfaceWindowMobileNotchStore } from '@@/pinia/surface_window_mobile_notch'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardLinkingStore } from '@@/pinia/whiteboard_linking'
import { useWhiteboardPermissionsStore } from '@@/pinia/whiteboard_permissions'
import { useWhiteboardPlayModeStore } from '@@/pinia/whiteboard_play_mode'
import { useWhiteboardScriptStore } from '@@/pinia/whiteboard_script'
import { useWhiteboardToolbarStore } from '@@/pinia/whiteboard_toolbar'
import { useWhiteboardUploadStore } from '@@/pinia/whiteboard_upload'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type { WhiteboardTool } from '@@/types/whiteboard'
import SurfaceWhiteboardModeSwitcher from '@@/vuecomponents//SurfaceWhiteboardModeSwitcher.vue'
import MembershipTierUpgradeModal from '@@/vuecomponents/MembershipTierUpgradeModal.vue'
import WhiteboardAttachmentPicker from '@@/vuecomponents/WhiteboardAttachmentPicker.vue'
import WhiteboardFrameBar from '@@/vuecomponents/WhiteboardFrameBar.vue'
import WhiteboardFrameBreakoutLinksPanel from '@@/vuecomponents/WhiteboardFrameBreakoutLinksPanel.vue'
import WhiteboardFrameShareViaAnotherAppPanel from '@@/vuecomponents/WhiteboardFrameShareViaAnotherAppPanel.vue'
import WhiteboardSecondaryMenu from '@@/vuecomponents/WhiteboardSecondaryMenu.vue'
import WhiteboardToolbar from '@@/vuecomponents/WhiteboardToolbar.vue'
import WhiteboardUploadStatusPill from '@@/vuecomponents/WhiteboardUploadStatusPill.vue'
import { shiftUpWhenOverflow } from '@@/vuecomposables/floating'
import { useTldrawCanvas } from '@@/vuecomposables/tldraw_canvas'
import { useWhiteboardShapeHandlers } from '@@/vuecomposables/whiteboard_shape_handlers'
import type { ComputePositionConfig } from '@floating-ui/vue'
import { flip, offset, shift } from '@floating-ui/vue'
import { Portal } from '@linusborg/vue-simple-portal'
import { cloneDeep, isEmpty, uniqueId } from 'lodash-es'
import { storeToRefs } from 'pinia'
import type { KeyBindingMap } from 'tinykeys'
import tinykeys from 'tinykeys'
import type { Editor, TLCameraOptions, TLPageId, TLShape, TLShapeId, VecLike } from 'tldraw'
import {
  Box,
  DefaultColorStyle,
  DefaultFillStyle,
  DefaultSizeStyle,
  GeoShapeGeoStyle,
  react,
  setUserPreferences,
} from 'tldraw'
import { computed, nextTick, onBeforeMount, onMounted, onUnmounted, ref, watch } from 'vue'
enum FrameContextMenuEvent {
  CopyLinkToFrame = 'CopyLinkToFrame',
  CopyFrameBreakoutLink = 'CopyFrameBreakoutLink',
  ChangeBackground = 'ChangeBackground',
  ShareAndPermissions = 'ShareAndPermissions',
  MoveFrameUp = 'MoveFrameUp',
  MoveFrameDown = 'MoveFrameDown',
  AddFrameAbove = 'AddFrameAbove',
  AddFrameBelow = 'AddFrameBelow',
  Duplicate = 'Duplicate',
  ClearFrame = 'ClearFrame',
  DeleteFrame = 'DeleteFrame',
}

interface FrameContextMenuEventPayload {
  action: FrameContextMenuEvent
  frameId: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'SurfaceContainerWhiteboard',
  setup(__props) {

const WhiteboardElementModifier = defineAsyncComponent(() => import('@@/vuecomponents/WhiteboardElementModifier.vue'))
const WhiteboardFrameSharePanel = defineAsyncComponent(() => import('@@/vuecomponents/WhiteboardFrameSharePanel.vue'))
const WhiteboardFrameBackgroundPanel = defineAsyncComponent(
  () => import('@@/vuecomponents/WhiteboardFrameBackgroundPanel.vue'),
)
const WhiteboardLinkingPanel = defineAsyncComponent(() => import('@@/vuecomponents/WhiteboardLinkingPanel.vue'))
const SurfaceWhiteboardHeader = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceWhiteboardHeader.vue'))
const WhiteboardScriptEditor = defineAsyncComponent(() => import('@@/vuecomponents/WhiteboardScriptEditor.vue'))
const WhiteboardPlayModeNavigationBar = defineAsyncComponent(
  () => import('@@/vuecomponents/WhiteboardPlayModeNavigationBar.vue'),
)
let tldrawEditor: Editor | undefined
const tldrawAppRef = ref()
const wallContainerRef = ref()

const whiteboardStore = useWhiteboardStore()
const globalAlertDialogStore = useGlobalAlertDialogStore()
const globalSnackbarStore = useGlobalSnackbarStore()
const { currentFrameId, isLoadingPortraitMobileLayout } = storeToRefs(whiteboardStore)
const whiteboardScriptStore = useWhiteboardScriptStore()
const globalConfirmationDialogStore = useGlobalConfirmationDialogStore()
const surfaceStore = useSurfaceStore()
const { xHeader } = storeToRefs(surfaceStore)
const { selectedTool, selectedSecondaryTool, toolStates, geoColorTldrawToolId } = storeToRefs(whiteboardStore)

const hasTldrawLoaded = ref(false)
const hasError = ref(false)
const isTransparentBackground = ref(false)

/**
 * MOBILE LANDSCAPE
 */

/** CANVAS RESIZE AND ZOOM */
function updateMobileNotchOnOrientationChangeAndResize() {
  useSurfaceWindowMobileNotchStore().updateSafeAreaInset()
  useSurfaceWindowMobileNotchStore().updateLandscapeNotch()
  surfaceContainerSizeStore.resizeSurfaceContainer({
    width: window.innerWidth,
    height: window.innerHeight,
  })
}
onMounted(() => {
  useSurfaceWindowMobileNotchStore().updateSafeAreaInset()
  useSurfaceWindowMobileNotchStore().updateLandscapeNotch()
  screen.orientation.addEventListener('change', updateMobileNotchOnOrientationChangeAndResize)
  window.addEventListener('resize', updateMobileNotchOnOrientationChangeAndResize)
})
onUnmounted(() => {
  screen.orientation.removeEventListener('change', updateMobileNotchOnOrientationChangeAndResize)
  window.removeEventListener('resize', updateMobileNotchOnOrientationChangeAndResize)
})
const surfaceContainerSizeStore = useSurfaceContainerSizeStore()
const {
  surfaceContainerWidth,
  surfaceContainerHeight,
  isLandscapePhone,
  isLandscapeTablet,
  isContainerSmallerThanTabletLandscape,
} = storeToRefs(surfaceContainerSizeStore)

const isHidingHeader = computed(() => {
  return isLandscapePhone.value && isTouchScreen()
})
const isSingleColumnToolbar = computed(() => {
  return isContainerSmallerThanTabletLandscape.value && !isLoadingPortraitMobileLayout.value
})

const whiteboardPlayModeStore = useWhiteboardPlayModeStore()
const canvasWidth = computed(() => {
  if (isLoadingPortraitMobileLayout.value) {
    return surfaceContainerWidth.value - 16 * 2
  }
  const W_H_RATIO = 16 / 9
  const MARGIN_FROM_SIDEBARS = 24
  const FRAME_BAR_WIDTH = 100
  const TOOLBAR_WIDTH = isSingleColumnToolbar.value ? 44 : 92

  let width =
    surfaceContainerWidth.value -
    2 * MARGIN_FROM_SIDEBARS -
    FRAME_BAR_WIDTH -
    TOOLBAR_WIDTH -
    useSurfaceWindowMobileNotchStore().safeAreaInsetLeft -
    useSurfaceWindowMobileNotchStore().safeAreaInsetRight
  if (whiteboardPlayModeStore.isPlayMode) {
    width = surfaceContainerWidth.value - 2 * 40 // 40 is padding
  }

  const height =
    surfaceContainerHeight.value -
    (isHidingHeader.value ? 0 : surfaceStore.headerHeight) -
    (whiteboardPlayModeStore.isPlayMode ? 56 : 32) -
    useSurfaceWindowMobileNotchStore().safeAreaInsetBottom

  if (width / height > W_H_RATIO) {
    return height * W_H_RATIO
  }
  return width
})
const insetInlineStart = computed(() => {
  // Why start-4.5 (18px)? 100px (frame bar) - 64px (toolbar) = 32px. Divide it by 2 to get 18.
  // 'start-4.5': !whiteboardPlayModeStore.isPlayMode,
  if (isLoadingPortraitMobileLayout.value) {
    return 0
  }
  if (whiteboardPlayModeStore.isPlayMode) {
    return 0
  }
  const FRAME_BAR_WIDTH = 100
  const TOOLBAR_WIDTH = isSingleColumnToolbar.value ? 52 : 92
  return (FRAME_BAR_WIDTH - TOOLBAR_WIDTH) / 2
})

const insetTop = computed(() => {
  if (!isLoadingPortraitMobileLayout.value) {
    return 0
  }
  const FRAME_BAR_HEIGHT = 100
  const TOOLBAR_HEIGHT = 58

  return (FRAME_BAR_HEIGHT - TOOLBAR_HEIGHT) / 2
})
const canvasHeight = computed(() => {
  const W_H_RATIO = 16 / 9

  return canvasWidth.value / W_H_RATIO
})

const isAllowingZooming = computed(() => {
  if ((isLandscapePhone || isLandscapeTablet) && isTouchScreen()) {
    return true
  }
  return !surfaceContainerSizeStore.isContainerBiggerThanTabletLandscape
})

const tldrawCameraOptions = computed<Partial<TLCameraOptions>>(() => ({
  isLocked: !isAllowingZooming.value, // lock the camera if we're not allowing zooming
  wheelBehavior: !isAllowingZooming.value ? 'none' : 'pan',
  zoomSteps: !isAllowingZooming.value ? [1] : [1, 2, 4, 8],
  zoomSpeed: !isAllowingZooming.value ? 0 : 1,
  constraints: {
    initialZoom: 'fit-y',
    baseZoom: 'fit-y',
    bounds: { x: 0, y: 0, w: CANVAS_WIDTH, h: CANVAS_HEIGHT },
    padding: { x: 0, y: 0 },
    origin: { x: 0, y: 0 },
    behavior: 'contain',
  },
}))
watch(isAllowingZooming, (isAllowing) => {
  if (isAllowing) {
    nextTick(() => {
      forceUpdateViewportScreenBounds()
    })
  }
})

const centerZoomToBounds = () => {
  nextTick(() => {
    // Needs to be unlocked to use zoomToBounds
    tldrawEditor?.setCameraOptions({
      isLocked: false,
    })
    tldrawEditor?.zoomToBounds(new Box(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT), { inset: 0 })
    tldrawEditor?.setCameraOptions({
      ...tldrawCameraOptions.value,
    })
  })
}

// Problem: The tldraw editor sometimes breaks the viewport bounds when the tab loses focus.
// Example: User switches to a different tab then comes back to whiteboard.
// When user tries to draw at point (0, 0), it will draw at point (20, 20) instead (arbitrary values to illustrate the offset).
// Solution: Whenever the window is focused, call `updateViewportScreenBounds` to reset the viewport.
// Reference: https://discord.com/channels/859816885297741824/1237588159806836786
const forceUpdateViewportScreenBounds = () => {
  nextTick(() => {
    const rect = document.getElementById('tldraw-app')?.getBoundingClientRect()
    if (rect) {
      centerZoomToBounds()
      const next = new Box(rect.left || rect.x, rect.top || rect.y, Math.max(rect.width, 1), Math.max(rect.height, 1))
      tldrawEditor?.updateViewportScreenBounds(next)
    }
  })
}

// When page changes, the newly opened page won't have the correct zoom level.
// So we need to re-zoom to the correct level.
watch([currentFrameId, canvasWidth, canvasHeight], () => {
  centerZoomToBounds()
})

/**
 * TOOL SHORTCUTS REGISTERING & DEREGISTERING
 */

/**
 * We don't want to trigger tool shortcuts while the user is typing
 */
const isInputFocused = (): boolean => isActiveElementAnInputField()

const surfaceSharePanelStore = useSurfaceSharePanelStore()
const surfaceSettingsStore = useSurfaceSettingsStore()
// Has to be a function to be able to check if the input is focused since it's not reactive
const checkIfShortcutsDisallowed = () =>
  isInputFocused() ||
  xScriptEditor.value ||
  xContextMenu.value ||
  surfaceSettingsStore.activePanel !== null ||
  surfaceSharePanelStore.xSurfaceSharePanel

const whiteboardPlayModeNavigationBarRef = ref<typeof WhiteboardPlayModeNavigationBar | null>(null)
function addShortcutsListener(): () => void {
  if (whiteboardPlayModeStore.isPlayMode) {
    if (whiteboardStore.isFrameBreakout) {
      // We should not let users navigate to other frames in breakout mode
      return () => {}
    }
    return tinykeys(window, {
      ArrowLeft: () => {
        handleAction(ActionType.GoToPreviousPage, () => true)
      },
      ArrowRight: () => {
        handleAction(ActionType.GoToNextPage, () => true)
      },
      ArrowUp: () => {
        handleAction(ActionType.GoToPreviousPage, () => true)
      },
      ArrowDown: () => {
        handleAction(ActionType.GoToNextPage, () => true)
      },
      PageUp: () => {
        handleAction(ActionType.GoToPreviousPage, () => true)
      },
      PageDown: () => {
        handleAction(ActionType.GoToNextPage, () => true)
      },
      Space: () => {
        // If user is on any UI on navigation bar and they hit the space key,
        // we only want the click event to happen and not the forward shortcut key event
        if (whiteboardPlayModeNavigationBarRef.value?.$el?.contains(document.activeElement)) {
          return
        }
        handleAction(ActionType.GoToNextPage, () => true)
      },
    })
  }
  const keybindingMap: KeyBindingMap = {}
  const primaryShortcuts = whiteboardStore.tools.map((tool) => tool.shortcut).filter((shortcut) => shortcut)
  const secondaryShortcuts = whiteboardStore.tools
    .map((tool) => tool.secondaryMenu?.items)
    .flat()
    .map((tool) => tool?.shortcut)
    .filter((shortcut) => shortcut)

  keybindingMap.Escape = () => {
    // Do not conditionally disable this shortcut otherwise there's no way to dismiss the menu with keys
    dismissContextMenu()
  }

  actionShortcuts.forEach((shortcut) => {
    keybindingMap[shortcut.kbd] = (event) => {
      if (!checkIfShortcutsDisallowed()) {
        event.preventDefault()
        handleBoardAction(shortcut.actionType)
      }
    }
  })

  primaryShortcuts.forEach((shortcut) => {
    if (!shortcut) {
      return
    }
    keybindingMap[shortcut] ??= () => {
      if (checkIfShortcutsDisallowed()) {
        return
      }
      if (whiteboardStore.selectedTool?.shortcut === shortcut) {
        // If the selected tool matches the pressed shortcut, try to find and select the next tool with the same shortcut
        // Used to support cases like having 2 (or more) draw tools, so user can toggle between all of them easily
        const selectedToolIndex = whiteboardStore.tools.findIndex((tool) => tool.id === whiteboardStore.selectedToolId)
        const nextToolId = whiteboardStore.tools.find(
          (tool, index) => tool.shortcut === shortcut && index > selectedToolIndex,
        )?.id
        if (nextToolId) {
          // If there isn't a next tool, we defer to the "Find first" logic below to cycle back to the first tool with the same shortcut.
          whiteboardStore.selectTool(nextToolId)
          // Clear the selected toolbar button since we don't have access to it from a shortcut
          whiteboardStore.setSelectedToolbarButtonEl(null)
          return
        }
      }
      // Find first
      const tool = whiteboardStore.tools.find((tool) => tool.shortcut === shortcut)
      if (tool && whiteboardStore.selectedToolId !== tool.id) {
        whiteboardStore.selectTool(tool.id)
        // Clear the selected toolbar button since we don't have access to it from a shortcut
        whiteboardStore.setSelectedToolbarButtonEl(null)
      }
    }
  })

  secondaryShortcuts.forEach((shortcut) => {
    if (!shortcut) {
      return
    }
    keybindingMap[shortcut] ??= () => {
      if (checkIfShortcutsDisallowed()) {
        return
      }
      const tool = whiteboardStore.tools.find((tool) =>
        tool.secondaryMenu?.items.find((item) => item?.shortcut === shortcut),
      )
      if (tool) {
        const secondaryTool = tool.secondaryMenu?.items.find((item) => item?.shortcut === shortcut)
        if (secondaryTool) {
          whiteboardStore.selectTool(tool.id)
          // Clear the selected toolbar button since we don't have access to it from a shortcut
          whiteboardStore.setSelectedToolbarButtonEl(null)
          whiteboardStore.selectSecondaryTool(secondaryTool.id)
        }
      }
    }
  })
  return tinykeys(window, keybindingMap)
}

let removeShortcutsListener = () => {}

onMounted(() => {
  window.addEventListener('focus', forceUpdateViewportScreenBounds)
  removeShortcutsListener = addShortcutsListener()
})

watch(whiteboardStore.tools, () => {
  removeShortcutsListener()
  removeShortcutsListener = addShortcutsListener()
})

onUnmounted(() => {
  window.removeEventListener('focus', forceUpdateViewportScreenBounds)
  removeShortcutsListener()
})

let checkCanEdit: (shape: TLShape) => boolean = () => false

function onTldrawCanvasMounted(editor: Editor) {
  /**
   * SYNC TLDRAW STATE TO VUE REACTIVE STATE
   *
   * All syncing for **global** state should be done here to ensure that
   * we only start rendering our UI after we have started listening
   * to all relevant global state to prevent race conditions.
   *
   * We do the syncing before anything else to ensure that there aren't any updates
   * to the editor state before we start listening.
   */

  /**
   * After using certain tools, e.g. geometry or shape, the editor will change the current tool back to Select automatically.
   * We track the change here and update our store value to match the editor value.
   *
   * Why not just check if the path starts with 'select'?
   * There are intermediate states (e.g. 'select.resizing') that the user could cancel from (e.g. Esc).
   * In those cases, we don't want to switch to Select yet to preserve the original behavior.
   * Also, we should not update the toolbar selected tool until the pointer is up.
   */
  react('set store tool to Select, if needed', () => {
    if (editor.isInAny('select') && whiteboardStore.selectedToolId !== 'select') {
      whiteboardStore.selectToolWithoutTriggeringTldraw('select')
    }
  })

  react('get selected object and check for script', () => {
    if (editor.isInAny('select')) {
      const selectedShapes = editor.getSelectedShapes()
      const selectedShape = selectedShapes.length === 1 ? selectedShapes[0] : null // If multiple shapes are selected, we don't show the script editor
      selectedShapeIdForScript.value = selectedShape?.id
      xOpenScriptEditorButton.value = selectedShape?.id != null
    } else {
      xOpenScriptEditorButton.value = false
    }
  })

  // @ts-ignore
  editor.on('interact:shape', (event: { shapeId; eventName }) => {
    if (event?.shapeId == null) {
      return
    }
    runScript(whiteboardScriptStore.scripts[event.shapeId], event.eventName)
    triggerLinking(editor, whiteboardLinkingStore.linkings[event.shapeId])
  })

  react('set only selected shape', () => {
    // It's important that we use getOnlySelectedShape and not getOnlySelectedShapeId,
    // so that this is reactive to all the changes within the shape, e.g. color changes

    // We have to clone the shape to prevent reactivity issues
    // Setting a value from tldraw to a ref will modify the original object to have vue reactivity, which breaks tldraw

    onlySelectedShape.value = cloneDeep(editor.getOnlySelectedShape())
  })

  react('set element modifier availability', () => {
    isElementModifierAvailable.value = editor.isInAny('select.idle')
  })

  react('update frame state with tldraw pages data', () => {
    const pages = editor.getPages()

    const newFrames = pages
      .map((page) => {
        return {
          id: page.id,
          name: page.name,
        }
      })
      .filter((page) => !!page?.id) // to filter out the observer object before assigning to state

    // Update frames and merge with existing frames img
    whiteboardStore.frames = newFrames
  })

  react('update current frame id', () => {
    whiteboardStore.currentFrameId = editor.getCurrentPageId()
  })

  react('set permission overrides based on page meta', () => {
    const pages = editor.getPages()
    const frameIdToFramePermissionOverride = {}
    pages.forEach((page) => {
      frameIdToFramePermissionOverride[page.id] = getFramePermissionOverride(page.id)
    })
    whiteboardPermissionsStore.frameIdToFramePermissionOverride = frameIdToFramePermissionOverride
  })

  react('set undo and redo capability', () => {
    const canUndo = editor.getCanUndo()
    const canRedo = editor.getCanRedo()
    whiteboardStore.canIUndo = canUndo
    whiteboardStore.canIRedo = canRedo
  })

  react('set transparent background based on current page meta', () => {
    const currentFrame = editor.getCurrentPage()
    const currentFrameMeta = currentFrame?.meta as PageMeta

    const currentBackgroundType = currentFrameMeta.backgroundType ?? BackgroundType.Plain
    isTransparentBackground.value = currentBackgroundType === BackgroundType.Frosted
  })

  react('hide secondary menu when drawing/editing objects', () => {
    if (editor.isInAny('draw.drawing', 'highlight.drawing', 'padlet-draw-eraser.erasing')) {
      xSecondaryMenu.value = false
    } else {
      xSecondaryMenu.value = true
    }
  })

  react('allow drawing with finger after using stylus', () => {
    if (editor.isInAny('select.idle') && editor.getInstanceState().isPenMode) {
      editor.updateInstanceState({ isPenMode: false })
    }
  })

  setEditorObject(editor)
  selectDefaultTool()
  initializeLinking(editor)
  listenToLinkingChanges(editor)

  /**
   * BREAKOUT LINK / FRAME LINK
   * Do it before we update the viewport screen bounds, so the camera is correctly bounded
   */
  let frameId
  if (whiteboardStore.isFrameBreakout) {
    const urlObj = new URL(window.location.href)
    const pathSegments = urlObj.pathname.split('/')
    // frameBreakoutIdentifier format: {wallHashidWithoutPrefix}-{frameId}
    // Note: The frameId itself may contain -
    const frameIdFromPath = pathSegments[pathSegments.length - 1].replace(
      `${surfaceStore.wallHashidWithoutPrefix}-`,
      '',
    )
    // Value is not automatically decoded unlike URLSearchParams
    frameId = decodeURIComponent(frameIdFromPath)
  } else {
    // Handle link to specific frame (not breakout), frameId is a query param instead
    const searchParams = new URLSearchParams(window.location.search)
    frameId = searchParams.get(FRAME_ID_QUERY_PARAM) || ''
  }

  // Update current frame if exists
  const matchingFrame = editor.getPage(frameId as TLPageId)
  if (matchingFrame) {
    // We don't want users to be able to undo to reach the initial frame
    editor.history.ignore(() => {
      editor.setCurrentPage(matchingFrame)
    })
  }

  // Lock to a finite canvas
  editor.setCameraOptions({
    ...tldrawCameraOptions.value,
  })
  centerZoomToBounds()

  // Add name to realtime cursor
  const name =
    surfaceStore.user.display_name ??
    surfaceStore.user.short_name ??
    surfaceStore.user.name ??
    surfaceStore.user.username
  setUserPreferences({
    id: String(surfaceStore.user.id) ?? uniqueId('anon-'),
    name,
  })

  const whiteboardShapeHandlers = useWhiteboardShapeHandlers(editor)
  checkCanEdit = whiteboardShapeHandlers.checkCanEdit

  // Upload with pasting and dropping

  editor.registerExternalContentHandler('files', async (content) => {
    content.files.forEach((file) => {
      whiteboardUploadStore.startNewFileUpload(file)
    })
  })

  function createShapeForTextOrUrl(text: string, point?: VecLike): void {
    try {
      const url = new URL(text)
      whiteboardUploadStore.startNewUrlUpload(url.toString())
    } catch (error) {
      // Not a URL
      editor.createShape({
        type: 'text',
        props: {
          text,
        },
        x: point?.x,
        y: point?.y,
      })
    }
  }

  editor.registerExternalContentHandler('embed', async (content) => {
    createShapeForTextOrUrl(content.url, content.point)
  })

  editor.registerExternalContentHandler('text', async (content) => {
    createShapeForTextOrUrl(content.text, content.point)
  })

  editor.registerExternalContentHandler('url', async (content) => {
    createShapeForTextOrUrl(content.url, content.point)
  })

  editor.registerExternalContentHandler('svg-text', async (content) => {
    const svgFile = new File([content.text], 'svg-text.svg', { type: 'image/svg+xml' })
    whiteboardUploadStore.startNewFileUpload(svgFile)
  })

  tldrawEditor = window.$tldrawEditor!
  whiteboardStore.isSnapMode = safeLocalStorage.getItem('whiteboard_snap_mode') === 'true'
  handleAction(whiteboardStore.isSnapMode ? ActionType.ToggleSnapOn : ActionType.ToggleSnapOff, () => true)
  hasTldrawLoaded.value = true
}

watch(
  () => whiteboardStore.isSnapMode,
  (isSnapMode) => {
    handleAction(isSnapMode ? ActionType.ToggleSnapOn : ActionType.ToggleSnapOff, () => true)
    safeLocalStorage.setItem('whiteboard_snap_mode', isSnapMode.toString())
  },
)

function onError(
  errorMessage: string = __('Sorry, there was an unexpected error. Please refresh the page and try again.'),
) {
  hasError.value = true
  globalSnackbarStore.setSnackbar({
    message: errorMessage,
    notificationType: SnackbarNotificationType.error,
    persist: true,
  })
}

useTldrawCanvas({
  elementId: 'tldraw-app',
  onMount: onTldrawCanvasMounted,
  onError,
})

watch(
  [selectedTool, selectedSecondaryTool, toolStates],
  () => {
    const newTool = whiteboardStore.selectedTool
    const newSecondaryTool = whiteboardStore.selectedSecondaryTool
    const newToolStates = whiteboardStore.toolStates
    if (!hasTldrawLoaded.value || tldrawEditor == null) {
      return
    }

    if (whiteboardStore.ignoreTldrawTrigger) {
      whiteboardStore.ignoreTldrawTrigger = false
      return
    }

    const tldrawToolId = newTool?.tldrawToolId ?? 'draw'
    // Geo tool
    // Switch to the geo tool in Tldraw depends on the selected secondary,
    // and set the color based on geo color menu

    if (tldrawToolId === 'geo') {
      const tldrawShapeId = whiteboardStore.selectedSecondaryTool?.tldrawToolId
      if (tldrawShapeId == null) {
        return
      }
      tldrawEditor.updateInstanceState(
        {
          stylesForNextShape: {
            ...tldrawEditor.getInstanceState().stylesForNextShape,
            [GeoShapeGeoStyle.id]: tldrawShapeId,
          },
        },
        { history: 'ignore' },
      )
      tldrawEditor.setStyleForNextShapes(DefaultSizeStyle, 'm')
      tldrawEditor.setCurrentTool('geo')
      tldrawEditor.setStyleForNextShapes(DefaultFillStyle, newToolStates.geo.fillStyle ?? 'semi')
      if (geoColorTldrawToolId.value) {
        tldrawEditor.setStyleForNextShapes(DefaultColorStyle, geoColorTldrawToolId.value)
      }
      return
    }

    // We also override using the tldraw patch to handle double clicks on canvas
    if (tldrawToolId === 'text') {
      tldrawEditor.setStyleForNextShapes(DefaultSizeStyle, 'xl')
    } else {
      tldrawEditor.setStyleForNextShapes(DefaultSizeStyle, 'm')
    }

    // Other tools
    // Switch to the tool in Tldraw, and set the color
    const color = newSecondaryTool?.tldrawToolId as TldrawDefaultColorStyleColor | undefined

    if (tldrawToolId === 'draw-nested') {
      if (newTool == null || newSecondaryTool == null) {
        return
      }
      tldrawEditor.setCurrentTool(newSecondaryTool.tldrawToolId ?? 'draw')
      const actualSecondaryToolColorId = whiteboardStore.toolCurrentColors[newTool.id]
      const tldrawToolId = getTldrawToolId(actualSecondaryToolColorId)
      tldrawEditor.setStyleForNextShapes(DefaultColorStyle, tldrawToolId as TldrawDefaultColorStyleColor)
      return
    }
    tldrawEditor.setCurrentTool(tldrawToolId)
    // Otherwise drawing a shape will fill after using geo
    tldrawEditor.setStyleForNextShapes(DefaultFillStyle, 'none')
    if (color) {
      tldrawEditor.setStyleForNextShapes(DefaultColorStyle, color)
    }
  },
  { immediate: true },
)

watch(geoColorTldrawToolId, (tldrawColor) => {
  if (!hasTldrawLoaded.value || tldrawEditor == null || tldrawColor == null) {
    return
  }
  tldrawEditor.setStyleForNextShapes(DefaultColorStyle, tldrawColor)
})

// Secondary Menu

const toolbar = ref<typeof WhiteboardToolbar | null>(null)
const toolbarEl = computed<HTMLElement | undefined>(() => toolbar.value?.$el)

const whiteboardToolbarStore = useWhiteboardToolbarStore()
const isRtl = dir() === 'rtl'
const secondaryMenuPopoverConfig = computed<Partial<ComputePositionConfig>>(() => {
  return !isLoadingPortraitMobileLayout.value
    ? {
        placement: isRtl ? 'right' : 'left',
        middleware: [
          offset({
            mainAxis: whiteboardToolbarStore.secondaryMenuMainAxisOffset,
            crossAxis: whiteboardToolbarStore.secondaryMenuCrossAxisOffset,
          }),
          shiftUpWhenOverflow({ distanceFromViewportEdges: 4 }),
        ],
      }
    : {
        placement: 'top',
        middleware: [
          offset({
            mainAxis: 8,
            crossAxis: whiteboardToolbarStore.secondaryMenuCrossAxisOffset,
          }),
          shift(),
        ],
      }
})

const xSecondaryMenu = ref(true)

// Script Editor
const isScriptEditorEnabled = ref(false)
const xScriptEditor = ref(false)
const selectedShapeIdForScript = ref<string | undefined>(undefined)
const xOpenScriptEditorButton = ref(false)
onMounted(() => {
  // Set isScriptEditorEnabled to true if the url params include script_editor=true
  // Color should be changed before tldraw is initialized
  changeAllTldrawDefaultColorStyleColor()
  const urlParams = new URLSearchParams(window.location.search)
  isScriptEditorEnabled.value = urlParams.has('script_editor')
  import('@@/bits/blockly/initialize').then((importedModule) => {
    const initializeBlockly = importedModule.default
    initializeBlockly()
  })
  whiteboardScriptStore.initializeWhiteboardScripts()
})

function saveScript(script: string) {
  if (!selectedShapeIdForScript.value) {
    return
  }
  whiteboardScriptStore.upsertScript(selectedShapeIdForScript.value, script)
  xScriptEditor.value = false
}
const initialScript = computed(() => {
  if (!selectedShapeIdForScript.value) {
    return ''
  }
  return whiteboardScriptStore.scripts[selectedShapeIdForScript.value]
})

/**
 * CONTEXT MENU
 */

const contextMenuGroups = ref<OzMenuRowsRow[][]>([])
const contextMenuRef = ref<typeof OzBasePopover | null>(null)
const contextMenuEl = computed(() => contextMenuRef.value?.$el)
const contextMenuAnchor = ref<Anchor>()
const xContextMenu = ref(false)

const MOD_KEY = device.mac ? '⌘' : 'Ctrl+'

function buildContextMenu(e: PointerEvent) {
  if (
    whiteboardStore.currentFrameId == null ||
    !whiteboardPermissionsStore.canIWriteFrame(whiteboardStore.currentFrameId)
  ) {
    return
  }
  if (whiteboardStore.selectedToolId !== 'select') {
    return
  }

  showContextMenu()
  contextMenuAnchor.value = { x: e.x, y: e.y }

  const selectedShapes = tldrawEditor?.getSelectedShapes() || []
  const hasSelectedShapes = selectedShapes.length > 0

  const copyRow = {
    icon: 'copy_outline',
    text: __('Copy'),
    eventPayload: ActionType.Copy,
    rightLabel: `${MOD_KEY}C`,
  }
  const pasteRow = {
    icon: 'clipboard_outline',
    text: __('Paste'),
    eventPayload: ActionType.Paste,
    rightLabel: `${MOD_KEY}V`,
  }
  const duplicateRow = {
    icon: 'duplicate_outline',
    text: __('Duplicate'),
    eventPayload: ActionType.Duplicate,
    rightLabel: `${MOD_KEY}D`,
  }
  const selectAllRow = {
    icon: 'select_all',
    text: __('Select all'),
    eventPayload: ActionType.SelectAll,
    rightLabel: '⇧A',
  }

  if (!hasSelectedShapes) {
    contextMenuGroups.value = [[pasteRow], [selectAllRow]]
    return
  }

  const areAllSelectedShapesFrozen = selectedShapes.every((shape) => shape.isLocked)
  if (areAllSelectedShapesFrozen) {
    contextMenuGroups.value = [
      [
        {
          icon: 'freeze',
          text: __('Unfreeze'),
          eventPayload: ActionType.ToggleFreeze,
        },
      ],
    ]
    return
  }

  const areAllSelectedShapesEditable = selectedShapes.every((shape) => checkCanEdit(shape))
  if (!areAllSelectedShapesEditable) {
    contextMenuGroups.value = [[copyRow, pasteRow, duplicateRow, selectAllRow]]
    return
  }

  contextMenuGroups.value = [
    [
      {
        icon: 'send_to_front_outline',
        text: __('Send to front'),
        eventPayload: ActionType.SendToFront,
        rightLabel: ']',
      },
      {
        icon: 'send_to_back_outline',
        text: __('Send to back'),
        eventPayload: ActionType.SendToBack,
        rightLabel: '[',
      },
    ],
    [
      {
        icon: 'link',
        text: __('Link to another card'),
        eventPayload: ActionType.Link,
        rightLabel: '⇧L',
        shouldShow: !whiteboardStore.isFrameBreakout,
      },
    ].filter((item) => item.shouldShow !== false),
    [
      {
        icon: 'scissors',
        text: __('Cut'),
        eventPayload: ActionType.Cut,
        rightLabel: `${MOD_KEY}X`,
      },
      copyRow,
      pasteRow,
      duplicateRow,
    ],
    [
      {
        icon: 'freeze',
        text: __('Freeze'),
        eventPayload: ActionType.ToggleFreeze,
        rightLabel: '⇧F',
      },
      {
        icon: 'delete',
        text: __('Delete'),
        eventPayload: ActionType.Delete,
        rightLabel: '⌫',
      },
    ],
    [selectAllRow],
  ].filter((group) => !isEmpty(group))
}

const handleClickToDismissContextMenu = (e) => {
  if (contextMenuEl.value?.contains(e.target)) {
    // Let the context menu click handlers dismiss the menu instead
    return
  }
  dismissContextMenu()
}

const showContextMenu = () => {
  xContextMenu.value = true
}

const dismissContextMenu = () => {
  xContextMenu.value = false
}

function handleBoardAction(actionType: ActionType) {
  if (actionType === ActionType.Link) {
    openLinkingPanel()
  }
  handleAction(actionType, checkCanEdit)
}

const handleSelectedAction = (_: unknown, contextMenuActionType: ActionType) => {
  handleBoardAction(contextMenuActionType)
  dismissContextMenu()
}

/**
 * ELEMENT COLOR/SHAPE MODIFIER
 */

const onlySelectedShape = ref<TLShape | null>(null)
const isElementModifierAvailable = ref(false)
const xElementModifier = computed(
  () =>
    isElementModifierAvailable.value &&
    !xContextMenu.value &&
    onlySelectedShape.value &&
    checkCanEdit(onlySelectedShape.value) &&
    isOnlySelectedShapeApproved.value,
)
const isOnlySelectedShapeApproved = computed(() =>
  onlySelectedShape.value ? isShapeApproved(onlySelectedShape.value) : true,
)

function mapTldrawColorToWhiteboardColor(
  secondaryMenuItems: WhiteboardTool[],
  tldrawColor: TldrawDefaultColorStyleColor,
): string {
  return secondaryMenuItems.find((item) => item.tldrawToolId === tldrawColor)?.id as string
}
const elementModifierShapeEl = ref()

watch(onlySelectedShape, () => {
  // Because we destroy the Vue instance inside PadletAttachmentShape whenever we switch the frame,
  // we need to delay the getElementById otherwise it will try to get the element before it has fully rendered
  // and the modifier will not be positioned correctly.
  // This is a hack but delaying it by putting it onto the task queue using setTimeout seems to work
  setTimeout(() => {
    elementModifierShapeEl.value = document.getElementById(onlySelectedShape.value?.id as string)?.children?.[0] ?? null
  }, 0)
})

const elementModifierShapeTool = computed<WhiteboardTool | null>(() => {
  if (onlySelectedShape.value?.typeName !== 'shape') {
    return null
  }
  // Find the tool that matches the selected shape
  const tool = ALL_TOOLS.find((tool) => tool.tldrawToolId === onlySelectedShape.value?.type)
  if (!tool) {
    return null
  }
  return {
    ...tool,
  }
})
const elementModifierShapeToolState = computed(() => {
  const tool = elementModifierShapeTool.value
  if (!tool) {
    return null
  }
  const secondaryMenuItems = tool.secondaryMenu?.items ?? []
  let currentSecondarySelectionId: string | undefined
  // Set the current selected secondary tool for the element modifier
  if (onlySelectedShape.value?.type !== 'geo') {
    // Non-geo
    const currentShapeColor: TldrawDefaultColorStyleColor | undefined = (onlySelectedShape.value?.props as any)?.color
    currentSecondarySelectionId = currentShapeColor
      ? mapTldrawColorToWhiteboardColor(secondaryMenuItems, currentShapeColor)
      : undefined
  } else {
    // Geo
    const currentGeoStyle: TldrawGeoShapeGeoStyle | undefined = (onlySelectedShape.value?.props as any)?.geo
    currentSecondarySelectionId = currentGeoStyle
  }
  return { currentSecondarySelectionId }
})
const selectedShapeColor = computed(() => {
  if (onlySelectedShape.value?.type === 'geo') {
    const currentShapeColor: TldrawDefaultColorStyleColor | undefined = (onlySelectedShape.value?.props as any)?.color
    if (!currentShapeColor) {
      return undefined
    }
    return mapTldrawColorToWhiteboardColor(geoToolColors, currentShapeColor)
  }
  return undefined
})
function editCurrentShape(selectedSecondaryTool: WhiteboardTool) {
  // None geo
  if (elementModifierShapeTool.value?.id !== 'geo') {
    editCurrentShapeColor(selectedSecondaryTool)
    return
  }
  // Geo
  tldrawEditor?.setStyleForSelectedShapes(
    GeoShapeGeoStyle,
    selectedSecondaryTool.tldrawToolId as TldrawGeoShapeGeoStyle,
  )

  onlySelectedShape.value = null
  nextTick(() => {
    onlySelectedShape.value = tldrawEditor?.getOnlySelectedShape() ?? null
  })
}
function editCurrentShapeColor(selectedSecondaryTool: WhiteboardTool) {
  tldrawEditor?.setStyleForSelectedShapes(
    DefaultColorStyle,
    selectedSecondaryTool.tldrawToolId as TldrawDefaultColorStyleColor,
  )
}

/**
 * ELEMENT MODERATION
 */

const xElementModerationApproveRejectModifier = computed(
  () =>
    isElementModifierAvailable.value &&
    !xContextMenu.value &&
    onlySelectedShape.value &&
    whiteboardPermissionsStore.canIApproveShape(onlySelectedShape.value),
)
const xElementModerationAwaitingApprovalInfo = computed(
  () =>
    isElementModifierAvailable.value &&
    !xContextMenu.value &&
    onlySelectedShape.value &&
    !isOnlySelectedShapeApproved.value &&
    surfaceStore.user.id === onlySelectedShape.value.meta.cb,
)

const rejectShape = () => {
  globalConfirmationDialogStore.openConfirmationDialog({
    ...ARMS_CROSSED_IN_X_ICON,
    title: __('Reject this object?'),
    body: __('Are you sure you want to reject this object? It will be deleted permanently.'),
    confirmButtonText: __('Delete'),
    cancelButtonText: __('Nevermind'),
    buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
    afterConfirmActions: [() => handleAction(ActionType.Delete, checkCanEdit)],
  })
}

/*
 * FRAME BAR
 */

const frameBarMaxHeight = computed(() => {
  // Height of canvas
  return Math.max(canvasHeight.value, toolbarEl.value?.getBoundingClientRect().height ?? 0)
})

// Hardcoded to Golden Ratio * (height of toolbar, differently on touch and non-touch)
const frameBarMinHeight = computed(() => Math.min((isTouchScreen() ? 268 : 247) * 1.618, frameBarMaxHeight.value))

/**
 * FRAME CONTEXT MENU
 */

const xFrameContextMenu = ref(false)
const frameContextMenuAnchor = ref<Anchor>()
const frameContextMenuGroups = ref<OzMenuRowsRow<FrameContextMenuEventPayload>[][]>([])

const buildFrameContextMenu = (anchor: Anchor, frameId: string) => {
  xFrameContextMenu.value = true
  frameContextMenuAnchor.value = anchor
  const canManageFrame = whiteboardPermissionsStore.canIManageFrame(frameId as TLPageId)
  const canEditFrame = whiteboardPermissionsStore.canIEditFrame(frameId as TLPageId)

  const menuGroups: OzMenuRowsRow[][] = [
    [
      {
        icon: 'link',
        text: __('Copy card shortcut link'),
        eventPayload: {
          action: FrameContextMenuEvent.CopyLinkToFrame,
          frameId,
        },
      },
      {
        icon: 'grid_link',
        text: __('Copy breakout link'),
        eventPayload: {
          action: FrameContextMenuEvent.CopyFrameBreakoutLink,
          frameId,
        },
      },
    ],
    [
      {
        icon: 'picture_frame',
        text: __('Change background'),
        eventPayload: { action: FrameContextMenuEvent.ChangeBackground, frameId },
        shouldShow: canEditFrame,
      },
      {
        icon: 'settings',
        text: whiteboardPermissionsStore.canIAdminister ? __('Sharing & permissions') : __('Sharing'),
        eventPayload: { action: FrameContextMenuEvent.ShareAndPermissions, frameId },
      },
    ].filter((item) => item.shouldShow !== false),
    [
      {
        icon: 'move_up',
        text: __('Move up'),
        eventPayload: {
          action: FrameContextMenuEvent.MoveFrameUp,
          frameId,
        },
        shouldShow: !whiteboardStore.isFirstFrame(frameId) && canManageFrame,
      },
      {
        icon: 'move_down',
        text: __('Move down'),
        eventPayload: {
          action: FrameContextMenuEvent.MoveFrameDown,
          frameId,
        },
        shouldShow: !whiteboardStore.isLastFrame(frameId) && canManageFrame,
      },
      {
        icon: 'new_frame_above',
        text: __('Add a card above'),
        eventPayload: {
          action: FrameContextMenuEvent.AddFrameAbove,
          frameId,
        },
        shouldShow: canManageFrame,
      },
      {
        icon: 'new_frame_below',
        text: __('Add a card below'),
        eventPayload: {
          action: FrameContextMenuEvent.AddFrameBelow,
          frameId,
        },
        shouldShow: canManageFrame,
      },
    ].filter((item) => item.shouldShow !== false),
    [
      {
        icon: 'duplicate_outline',
        text: __('Duplicate'),
        eventPayload: {
          action: FrameContextMenuEvent.Duplicate,
          frameId,
        },
        shouldShow: canManageFrame,
      },
      {
        icon: 'clear_frame',
        text: __('Clear all objects'),
        eventPayload: {
          action: FrameContextMenuEvent.ClearFrame,
          frameId,
        },
        shouldShow: canManageFrame,
      },
    ].filter((item) => item.shouldShow !== false),
    [
      {
        icon: 'delete',
        rowStyle: OzMenuRowsRowStyle.Danger,
        text: __('Delete'),
        eventPayload: {
          action: FrameContextMenuEvent.DeleteFrame,
          frameId,
        },
        shouldShow: canManageFrame,
      },
    ].filter((item) => item.shouldShow !== false),
  ]

  frameContextMenuGroups.value = menuGroups.filter((group) => !isEmpty(group))
}

const handleFrameContextMenuAction = (_: unknown, payload: FrameContextMenuEventPayload) => {
  const frameId = payload.frameId as TLPageId

  switch (payload.action) {
    case FrameContextMenuEvent.CopyLinkToFrame: {
      whiteboardStore.copyLinkToFrame(frameId)
      break
    }
    case FrameContextMenuEvent.CopyFrameBreakoutLink: {
      whiteboardStore.copyFrameBreakoutLink(frameId)
      break
    }
    case FrameContextMenuEvent.ChangeBackground: {
      whiteboardStore.panelFrameId = frameId
      whiteboardStore.xFrameBackgroundPanel = true
      break
    }
    case FrameContextMenuEvent.ShareAndPermissions: {
      whiteboardStore.panelFrameId = frameId
      whiteboardStore.xFrameSharePanel = true
      break
    }
    case FrameContextMenuEvent.MoveFrameUp: {
      whiteboardStore.moveFrameUp(frameId)
      break
    }
    case FrameContextMenuEvent.MoveFrameDown: {
      whiteboardStore.moveFrameDown(frameId)
      break
    }
    case FrameContextMenuEvent.AddFrameAbove: {
      whiteboardStore.addFrameAbove(frameId)
      break
    }
    case FrameContextMenuEvent.AddFrameBelow: {
      whiteboardStore.addFrameBelow(frameId)
      break
    }
    case FrameContextMenuEvent.Duplicate: {
      if (!whiteboardStore.getCanAddFrame()) {
        break
      }
      tldrawEditor?.batch(() => {
        const currentPage = tldrawEditor?.getCurrentPage()
        tldrawEditor?.duplicatePage(frameId)
        const newPage = tldrawEditor?.getCurrentPage()
        if (newPage) {
          // Editor.duplicatePage does not copy page meta by default
          tldrawEditor?.updatePage({ ...newPage, meta: currentPage?.meta })
        }
      })
      break
    }
    case FrameContextMenuEvent.ClearFrame: {
      const shapeIds = tldrawEditor?.getPageShapeIds(frameId) || []
      tldrawEditor?.deleteShapes([...shapeIds])
      break
    }
    case FrameContextMenuEvent.DeleteFrame: {
      if (whiteboardStore.isSingleFrame) {
        globalAlertDialogStore.openAlertDialog({
          title: __('Card cannot be deleted'),
          body: __('At least one card is required. Please create a new card if you want to delete this one.'),
          closeButtonText: __('OK'),
          shouldFadeIn: true,
        })
        return
      }

      globalConfirmationDialogStore.openConfirmationDialog({
        title: __('Delete this card?'),
        body: __('Are you sure you want to delete this card? This cannot be undone!'),
        confirmButtonText: __('Delete'),
        cancelButtonText: __('Nevermind'),
        buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
        afterConfirmActions: [() => whiteboardStore.deleteFrame(frameId)],
      })
      break
    }
    default:
      break
  }

  xFrameContextMenu.value = false
}

/*
 * PERMISSION
 */

const whiteboardPermissionsStore = useWhiteboardPermissionsStore()
const { frameIdToFramePermissionOverride } = storeToRefs(whiteboardPermissionsStore)

watch(
  [frameIdToFramePermissionOverride, currentFrameId],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ([_, currentFrameId]) => {
    // We need the allowed tools to change in realtime whenever the page changes or the frame permissions change
    if (currentFrameId == null) return
    if (!whiteboardPermissionsStore.isToolAllowed(whiteboardStore.selectedToolId, currentFrameId)) {
      selectFirstAllowedTool()
    }
  },
  { immediate: true },
)

function selectFirstAllowedTool(): void {
  const tldrawEditor = getEditorObject()
  if (!tldrawEditor) {
    return
  }
  if (useWhiteboardPlayModeStore().isPlayMode) {
    tldrawEditor.setCurrentTool(VIEWER_TOOL_IDS[0])
    return
  }
  const allowedToolIds = whiteboardPermissionsStore.canIWriteFrame(tldrawEditor.getCurrentPageId())
    ? WRITER_TOOL_IDS
    : VIEWER_TOOL_IDS
  whiteboardStore.selectTool(allowedToolIds[0])
}

function selectDefaultTool(): void {
  const tldrawEditor = getEditorObject()
  if (!tldrawEditor) {
    return
  }
  let defaultToolId = 'select'
  if (!whiteboardPermissionsStore.canIWriteFrame(tldrawEditor.getCurrentPageId())) {
    defaultToolId = VIEWER_TOOL_IDS[0]
  }
  if (
    whiteboardPermissionsStore.canIWriteFrame(tldrawEditor.getCurrentPageId()) &&
    tldrawEditor.getCurrentPageShapeIds().size === 0
  ) {
    defaultToolId = isTouchScreen() ? 'draw-nested' : 'text'
  }
  whiteboardStore.selectTool(defaultToolId)
}

/*
 * TOOLBAR
 */

const xToolbar = computed(() => whiteboardPermissionsStore.canIWriteFrame(whiteboardStore.currentFrameId as TLPageId))

/** UPLOAD PILLS */

const whiteboardUploadStore = useWhiteboardUploadStore()
const uploadPillRefs = ref<typeof WhiteboardUploadStatusPill[]>([])
function stopPeekingUploadPills() {
  uploadPillRefs.value.forEach((pill) => pill.stopPeeking())
}
function cancelStoppingPeekingUploadPills() {
  uploadPillRefs.value.forEach((pill) => pill.cancelStoppingPeeking())
}

function clearFinishedUploadPill(upload) {
  delete whiteboardUploadStore.uploads[upload.uploadKey]
  whiteboardUploadStore.uploads = { ...whiteboardUploadStore.uploads }
}

/** LINKING & LINKING PANEL */
const whiteboardLinkingStore = useWhiteboardLinkingStore()
function openLinkingPanel() {
  if (!tldrawEditor) return
  const shapeId = tldrawEditor.getOnlySelectedShapeId()
  if (!shapeId) return
  whiteboardLinkingStore.openLinkingPanel(shapeId)
}

const xLinkingMenu = computed(
  () =>
    isElementModifierAvailable.value &&
    !xContextMenu.value &&
    onlySelectedShape.value &&
    checkCanEdit(onlySelectedShape.value) &&
    whiteboardLinkingStore.checkIfLinkingExists(onlySelectedShape.value.id),
)

/** PLAY MODE */

onBeforeMount(() => {
  const searchParams = new URLSearchParams(window.location.search)
  const isLoadedInPlayMode = (searchParams.get('play') || '') === '1'
  whiteboardPlayModeStore.isPlayMode = isLoadedInPlayMode
})
onMounted(() => {
  if (whiteboardPlayModeStore.isPlayMode) {
    whiteboardStore.selectTool('interact')
  }
})
watch(
  () => whiteboardPlayModeStore.isPlayMode,
  (isPlayMode) => {
    if (isPlayMode) {
      whiteboardStore.selectTool('interact')
    } else {
      selectDefaultTool()
    }
    removeShortcutsListener()
    removeShortcutsListener = addShortcutsListener()
  },
)

/**
 * HIDING UNAPPROVED SHAPES AND INDICATORS WHEN INTERACTING / IN PLAY MODE
 */
const shapesHidingTemporarily = ref(new Set<TLShapeId>())
const appendedCss = ref<HTMLStyleElementWrapper | null>(null)

function hideAllUnapprovedShapesTemporarily(): void {
  const editor = getEditorObject()
  if (editor == null) {
    return
  }
  const shapes = editor
    .getPages()
    .flatMap((page) => Array.from(editor.getPageShapeIds(page.id)))
    .map((shapeId) => editor.getShape(shapeId)) as TLShape[]
  shapes.forEach((shape) => {
    if (!isShapeApproved(shape)) {
      document.getElementById(shape.id)?.classList.add('hidden')
      shapesHidingTemporarily.value.add(shape.id)
    }
  })
}

function unhideAllUnapprovedShapes(): void {
  const editor = getEditorObject()
  if (editor == null) {
    return
  }
  shapesHidingTemporarily.value.forEach((shapeId) => {
    document.getElementById(shapeId)?.classList.remove('hidden')
  })
  shapesHidingTemporarily.value.clear()
}

function hideAllUnapprovedIndicators(): void {
  // isHidingUnapprovedIndicators.value = true
  appendedCss.value = appendCssToDocumentHead(`.unapproved-indicator { display: none !important; }`)
}

function unhideAllUnapprovedIndicators(): void {
  // isHidingUnapprovedIndicators.value = false
  appendedCss?.value?.remove()
}

watch(selectedTool, (newTool) => {
  const tldrawToolId = newTool?.tldrawToolId

  if (tldrawToolId === 'interact') {
    hideAllUnapprovedShapesTemporarily()
    hideAllUnapprovedIndicators()
  } else {
    unhideAllUnapprovedShapes()
    unhideAllUnapprovedIndicators()
  }
})

/**
 * PLANS
 */
const personalPlansStore = usePersonalPlansStore()

/**
 * POPOVER HEIGHT
 */

const { windowHeight } = storeToRefs(useWindowSizeStore())

const PADDING_FROM_EDGES = 12
const maxPopoverHeight = computed(() => windowHeight.value - PADDING_FROM_EDGES * 2)

return { __sfc: true,WhiteboardElementModifier, WhiteboardFrameSharePanel, WhiteboardFrameBackgroundPanel, WhiteboardLinkingPanel, SurfaceWhiteboardHeader, WhiteboardScriptEditor, WhiteboardPlayModeNavigationBar, tldrawEditor, tldrawAppRef, wallContainerRef, whiteboardStore, globalAlertDialogStore, globalSnackbarStore, currentFrameId, isLoadingPortraitMobileLayout, whiteboardScriptStore, globalConfirmationDialogStore, surfaceStore, xHeader, selectedTool, selectedSecondaryTool, toolStates, geoColorTldrawToolId, hasTldrawLoaded, hasError, isTransparentBackground, updateMobileNotchOnOrientationChangeAndResize, surfaceContainerSizeStore, surfaceContainerWidth, surfaceContainerHeight, isLandscapePhone, isLandscapeTablet, isContainerSmallerThanTabletLandscape, isHidingHeader, isSingleColumnToolbar, whiteboardPlayModeStore, canvasWidth, insetInlineStart, insetTop, canvasHeight, isAllowingZooming, tldrawCameraOptions, centerZoomToBounds, forceUpdateViewportScreenBounds, isInputFocused, surfaceSharePanelStore, surfaceSettingsStore, checkIfShortcutsDisallowed, whiteboardPlayModeNavigationBarRef, addShortcutsListener, removeShortcutsListener, checkCanEdit, onTldrawCanvasMounted, onError, toolbar, toolbarEl, whiteboardToolbarStore, isRtl, secondaryMenuPopoverConfig, xSecondaryMenu, isScriptEditorEnabled, xScriptEditor, selectedShapeIdForScript, xOpenScriptEditorButton, saveScript, initialScript, contextMenuGroups, contextMenuRef, contextMenuEl, contextMenuAnchor, xContextMenu, MOD_KEY, buildContextMenu, handleClickToDismissContextMenu, showContextMenu, dismissContextMenu, handleBoardAction, handleSelectedAction, onlySelectedShape, isElementModifierAvailable, xElementModifier, isOnlySelectedShapeApproved, mapTldrawColorToWhiteboardColor, elementModifierShapeEl, elementModifierShapeTool, elementModifierShapeToolState, selectedShapeColor, editCurrentShape, editCurrentShapeColor, xElementModerationApproveRejectModifier, xElementModerationAwaitingApprovalInfo, rejectShape, frameBarMaxHeight, frameBarMinHeight, FrameContextMenuEvent, xFrameContextMenu, frameContextMenuAnchor, frameContextMenuGroups, buildFrameContextMenu, handleFrameContextMenuAction, whiteboardPermissionsStore, frameIdToFramePermissionOverride, selectFirstAllowedTool, selectDefaultTool, xToolbar, whiteboardUploadStore, uploadPillRefs, stopPeekingUploadPills, cancelStoppingPeekingUploadPills, clearFinishedUploadPill, whiteboardLinkingStore, openLinkingPanel, xLinkingMenu, shapesHidingTemporarily, appendedCss, hideAllUnapprovedShapesTemporarily, unhideAllUnapprovedShapes, hideAllUnapprovedIndicators, unhideAllUnapprovedIndicators, personalPlansStore, windowHeight, PADDING_FROM_EDGES, maxPopoverHeight, isTouchScreen, __, approveShape, OzBasePopover, OzBasePopoverColors, OzBox, OzBoxColors, OzContainedButton, OzContainedButtonColorScheme, OzContainedButtonSizePreset, OzDivider, OzDividerColorScheme, OzMenuRows, OzMenuRowsFocusStyle, OzMenuRowsSizePreset, OzPlainButton, OzPlainButtonColorScheme, OzPlainButtonSizePreset, OzPopover, SurfaceWhiteboardModeSwitcher, MembershipTierUpgradeModal, WhiteboardAttachmentPicker, WhiteboardFrameBar, WhiteboardFrameBreakoutLinksPanel, WhiteboardFrameShareViaAnotherAppPanel, WhiteboardSecondaryMenu, WhiteboardToolbar, WhiteboardUploadStatusPill, shiftUpWhenOverflow, flip, offset, Portal }
}

})